/**
 * LOGOS v5.0 — Energy Landscape Map
 * 2D visualization: Λ (x-axis) vs S (y-axis) with dual attractors
 * Based on Watson framework + Theorem 3 (Teleonomy)
 */

import React, { useRef, useEffect, useState } from 'react';

const COLORS = {
  bg: '#0F0F1A',
  border: '#2D2D44',
  text: '#E0E0F0',
  textDim: '#8888AA',
  aPlus: '#10B981',
  aMinus: '#EF4444',
  separatrix: '#F59E0B',
  user: '#C084FC',
  grid: '#1A1A2E',
};

export default function EnergyLandscapeMap({ lambda, entropy, width: propWidth = 340, height: propHeight = 280 }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(propWidth);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) { const w = e.contentRect.width; if (w > 0) setContainerW(w); }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);
  const width = Math.min(containerW, propWidth);
  const height = Math.round(width * (propHeight / propWidth));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 2;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);

    const pad = { top: 30, right: 20, bottom: 35, left: 40 };
    const w = width - pad.left - pad.right;
    const h = height - pad.top - pad.bottom;

    // Clear
    ctx.fillStyle = COLORS.bg;
    ctx.fillRect(0, 0, width, height);

    // Grid
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 10; i++) {
      const x = pad.left + (i / 10) * w;
      const y = pad.top + (i / 10) * h;
      ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + h); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + w, y); ctx.stroke();
    }

    // Attractor A⁺ (high Λ, low S) — green glow → upper-right
    const aPlusX = pad.left + 0.85 * w;
    const aPlusY = pad.top + 0.10 * h;
    const gradPlus = ctx.createRadialGradient(aPlusX, aPlusY, 0, aPlusX, aPlusY, 60);
    gradPlus.addColorStop(0, 'rgba(16,185,129,0.35)');
    gradPlus.addColorStop(0.5, 'rgba(16,185,129,0.10)');
    gradPlus.addColorStop(1, 'transparent');
    ctx.fillStyle = gradPlus;
    ctx.fillRect(0, 0, width, height);

    // Attractor A⁻ (low Λ, high S) — red glow → lower-left
    const aMinusX = pad.left + 0.10 * w;
    const aMinusY = pad.top + 0.85 * h;
    const gradMinus = ctx.createRadialGradient(aMinusX, aMinusY, 0, aMinusX, aMinusY, 60);
    gradMinus.addColorStop(0, 'rgba(239,68,68,0.30)');
    gradMinus.addColorStop(0.5, 'rgba(239,68,68,0.08)');
    gradMinus.addColorStop(1, 'transparent');
    ctx.fillStyle = gradMinus;
    ctx.fillRect(0, 0, width, height);

    // Separatrix line (Λ ≈ 0.35)
    const sepX = pad.left + 0.35 * w;
    ctx.strokeStyle = COLORS.separatrix + '60';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(sepX, pad.top);
    ctx.lineTo(sepX, pad.top + h);
    ctx.stroke();
    ctx.setLineDash([]);

    // Separatrix label
    ctx.fillStyle = COLORS.separatrix + 'AA';
    ctx.font = '9px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Separatriz', sepX, pad.top - 5);
    ctx.fillText('Λ=0.35', sepX, pad.top - 15);

    // Attractor labels
    ctx.fillStyle = COLORS.aPlus;
    ctx.font = 'bold 11px Arial';
    ctx.fillText('A⁺', aPlusX, aPlusY - 10);
    ctx.font = '9px Arial';
    ctx.fillText('Logos', aPlusX, aPlusY + 2);

    ctx.fillStyle = COLORS.aMinus;
    ctx.font = 'bold 11px Arial';
    ctx.fillText('A⁻', aMinusX, aMinusY - 10);
    ctx.font = '9px Arial';
    ctx.fillText('Entropía', aMinusX, aMinusY + 2);

    // USER POSITION — pulsing dot
    const userX = pad.left + Math.max(0, Math.min(1, lambda)) * w;
    const userY = pad.top + Math.max(0, Math.min(1, entropy)) * h;

    // Glow
    const t = Date.now() * 0.003;
    const pulseR = 8 + Math.sin(t) * 3;
    const userGrad = ctx.createRadialGradient(userX, userY, 0, userX, userY, pulseR * 2.5);
    userGrad.addColorStop(0, 'rgba(192,132,252,0.6)');
    userGrad.addColorStop(0.5, 'rgba(192,132,252,0.15)');
    userGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = userGrad;
    ctx.fillRect(userX - 30, userY - 30, 60, 60);

    // Dot
    ctx.beginPath();
    ctx.arc(userX, userY, 5, 0, Math.PI * 2);
    ctx.fillStyle = COLORS.user;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // User label
    ctx.fillStyle = COLORS.text;
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('TÚ', userX, userY - 12);

    // Axis labels
    ctx.fillStyle = COLORS.textDim;
    ctx.font = '10px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Λ (Alineación con Logos) →', pad.left + w / 2, height - 5);

    ctx.save();
    ctx.translate(12, pad.top + h / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('S (Entropía) ↓', 0, 0);
    ctx.restore();

    // Axis values
    ctx.font = '8px Arial';
    ctx.fillStyle = COLORS.textDim;
    ctx.textAlign = 'center';
    [0, 0.25, 0.5, 0.75, 1].forEach((v) => {
      ctx.fillText(v.toFixed(2), pad.left + v * w, pad.top + h + 12);
    });
    ctx.textAlign = 'right';
    [0, 0.25, 0.5, 0.75, 1].forEach((v) => {
      ctx.fillText(v.toFixed(2), pad.left - 5, pad.top + v * h + 3);
    });

  }, [lambda, entropy, width, height]);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{ width, height, maxWidth: '100%', borderRadius: '12px', border: `1px solid ${COLORS.border}` }}
      />
      <div style={{
        position: 'absolute', bottom: '8px', right: '8px',
        background: 'rgba(15,15,26,0.85)', borderRadius: '8px', padding: '6px 10px',
        fontSize: '11px', color: COLORS.textDim,
      }}>
        Λ={lambda.toFixed(2)} S={entropy.toFixed(2)}
        <span style={{
          marginLeft: '6px',
          color: lambda > 0.35 ? COLORS.aPlus : COLORS.aMinus,
          fontWeight: 600,
        }}>
          → {lambda > 0.35 ? 'A⁺' : 'A⁻'}
        </span>
      </div>
    </div>
  );
}
